import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Calculator, 
  Clock, 
  MapPin, 
  Search, 
  Wrench, 
  ArrowRight, 
  ShieldCheck, 
  Laptop, 
  DollarSign, 
  FileText, 
  CreditCard,
  CheckCircle2,
  HardDrive,
  RefreshCw,
  Wifi,
  Printer,
  Database,
  Building2,
  Server,
  Network,
  Globe,
  Sparkles,
  Smartphone,
  Cpu,
  Camera,
  X
} from 'lucide-react';

export interface EstimatedJob {
  id: string;
  title: string;
  category: 'all' | 'pc' | 'mobile' | 'network' | 'data' | 'business' | 'web';
  categoryLabel: string;
  icon: React.ReactNode;
  typicalHoursMin: number;
  typicalHoursMax: number;
  timeDisplay: string;
  rateType: 'standard' | 'consulting' | 'project';
  benchRecommended?: boolean;
  fixedPrice?: number;
  description: string;
}

export const ESTIMATED_JOBS: EstimatedJob[] = [
  {
    id: 'virus-cleanup',
    title: 'Virus, Malware & Spyware Removal',
    category: 'pc',
    categoryLabel: 'PC & Laptop Repair',
    icon: <ShieldCheck className="w-5 h-5 text-emerald-400" />,
    typicalHoursMin: 2,
    typicalHoursMax: 3,
    timeDisplay: '2 – 3 Hours',
    rateType: 'standard',
    benchRecommended: true,
    description: 'Scans and removes viruses, spyware, pop-ups, and malware from your computer.'
  },
  {
    id: 'ssd-upgrade',
    title: 'SSD Solid State Drive Upgrade & System Clone',
    category: 'pc',
    categoryLabel: 'Hardware & Speed',
    icon: <HardDrive className="w-5 h-5 text-emerald-400" />,
    typicalHoursMin: 2,
    typicalHoursMax: 3,
    timeDisplay: '2 – 3 Hours',
    rateType: 'standard',
    benchRecommended: true,
    description: 'Replaces your slow hard drive with a fast solid-state drive and transfers your files.'
  },
  {
    id: 'os-reinstall',
    title: 'Operating System Clean Reinstall & Optimization',
    category: 'pc',
    categoryLabel: 'System Rebuild',
    icon: <RefreshCw className="w-5 h-5 text-emerald-400" />,
    typicalHoursMin: 2,
    typicalHoursMax: 3,
    timeDisplay: '2 – 3 Hours',
    rateType: 'standard',
    benchRecommended: true,
    description: 'Reinstalls Windows or Linux fresh with updated drivers and security updates.'
  },
  {
    id: 'pc-tuneup',
    title: 'Comprehensive PC Tune-Up & Dust Cleanout',
    category: 'pc',
    categoryLabel: 'Maintenance',
    icon: <Wrench className="w-5 h-5 text-emerald-400" />,
    typicalHoursMin: 1,
    typicalHoursMax: 1,
    timeDisplay: '1 Hour (1-Hr Minimum)',
    rateType: 'standard',
    benchRecommended: true,
    description: 'Cleans out internal dust and speeds up slow startup programs.'
  },
  {
    id: 'software-installs',
    title: 'Software Installs & Configuration',
    category: 'pc',
    categoryLabel: 'Software & Apps',
    icon: <Cpu className="w-5 h-5 text-emerald-400" />,
    typicalHoursMin: 1,
    typicalHoursMax: 1.5,
    timeDisplay: '1 – 1.5 Hours',
    rateType: 'standard',
    benchRecommended: false,
    description: 'Installs and configures desktop software, productivity applications, antivirus, accounting packages, and specialized tools.'
  },
  {
    id: 'phone-migration',
    title: 'Cell Phone Migration & Data Transfer',
    category: 'mobile',
    categoryLabel: 'Mobile & Data Transfer',
    icon: <Smartphone className="w-5 h-5 text-cyan-400" />,
    typicalHoursMin: 1,
    typicalHoursMax: 2,
    timeDisplay: '1 – 2 Hours',
    rateType: 'standard',
    benchRecommended: true,
    description: 'Complete data transfer of contacts, photos, messages, apps, and accounts from your old phone to your new device.'
  },
  {
    id: 'wifi-setup',
    title: 'Home / Office Wi-Fi, Mesh & Router Setup',
    category: 'network',
    categoryLabel: 'Wi-Fi & Networking',
    icon: <Wifi className="w-5 h-5 text-cyan-400" />,
    typicalHoursMin: 1,
    typicalHoursMax: 1.5,
    timeDisplay: '1 – 1.5 Hours',
    rateType: 'standard',
    description: 'Sets up your Wi-Fi router or mesh system and reconnects your devices.'
  },
  {
    id: 'ethernet-repairs',
    title: 'Ethernet Wiring Repairs & Cable Termination',
    category: 'network',
    categoryLabel: 'Wiring & Repairs',
    icon: <Wrench className="w-5 h-5 text-cyan-400" />,
    typicalHoursMin: 1,
    typicalHoursMax: 2,
    timeDisplay: '1 – 2 Hours',
    rateType: 'standard',
    description: 'Fixes damaged CAT5e/6 cables, terminates loose RJ45 connectors, replaces wall jacks, and certifies link connectivity.'
  },
  {
    id: 'dns-website-filtering',
    title: 'DNS Website Filtering / Family Internet Controls',
    category: 'network',
    categoryLabel: 'Security & Parental Controls',
    icon: <ShieldCheck className="w-5 h-5 text-cyan-400" />,
    typicalHoursMin: 1,
    typicalHoursMax: 1.5,
    timeDisplay: '1 – 1.5 Hours',
    rateType: 'standard',
    description: 'Configures router-level or device-level DNS content filtering, adult website blocking, malicious domain protection, and family screen time controls.'
  },
  {
    id: 'printer-setup',
    title: 'Printer & Scanner Setup / Network Sharing',
    category: 'network',
    categoryLabel: 'Peripherals',
    icon: <Printer className="w-5 h-5 text-cyan-400" />,
    typicalHoursMin: 1,
    typicalHoursMax: 1,
    timeDisplay: '1 Hour (1-Hr Minimum)',
    rateType: 'standard',
    description: 'Connects your printer to Wi-Fi and gets it printing and scanning.'
  },
  {
    id: 'data-backup',
    title: 'Personal Data Backup & Drive Migration',
    category: 'data',
    categoryLabel: 'Data & Storage',
    icon: <Database className="w-5 h-5 text-brand-400" />,
    typicalHoursMin: 1,
    typicalHoursMax: 3,
    timeDisplay: '1 – 3 Hours',
    rateType: 'standard',
    benchRecommended: true,
    description: 'Copies your important photos and documents to a backup drive or cloud storage.'
  },
  {
    id: 'small-business-workstations',
    title: 'Small Business Workstation & Shared Network Setup',
    category: 'business',
    categoryLabel: 'Business Systems',
    icon: <Building2 className="w-5 h-5 text-amber-400" />,
    typicalHoursMin: 2,
    typicalHoursMax: 4,
    timeDisplay: '2 – 4 Hours',
    rateType: 'standard',
    description: 'Sets up office computers with shared folders and network printer access.'
  },
  {
    id: 'network-vlan-build',
    title: 'Complex Network Builds & VLAN / WAN Engineering',
    category: 'network',
    categoryLabel: 'Network Engineering',
    icon: <Network className="w-5 h-5 text-amber-400" />,
    typicalHoursMin: 3,
    typicalHoursMax: 6,
    timeDisplay: '3 – 6 Hours',
    rateType: 'consulting',
    description: 'Managed switch architecture, multi-VLAN segmentation for POS/guest isolation, and high-performance routing.'
  },
  {
    id: 'server-hypervisor',
    title: 'Server Deployment / Hypervisor & Active Directory',
    category: 'business',
    categoryLabel: 'Network Engineering',
    icon: <Server className="w-5 h-5 text-amber-400" />,
    typicalHoursMin: 3,
    typicalHoursMax: 8,
    timeDisplay: '3 – 8 Hours',
    rateType: 'consulting',
    description: 'Installs, configures, and maintains Windows or Linux servers, hypervisors, and Active Directory domains.'
  },
  {
    id: 'cabling-rack',
    title: 'Structured CAT6 Cabling & Rack Dressing',
    category: 'business',
    categoryLabel: 'Network Engineering',
    icon: <Cpu className="w-5 h-5 text-amber-400" />,
    typicalHoursMin: 3,
    typicalHoursMax: 8,
    timeDisplay: '3 – 8 Hours',
    rateType: 'consulting',
    description: 'Certified CAT5e/6/7 data cabling, patch panel termination, and professional server rack cable dressing.'
  },
  {
    id: 'camera-installations',
    title: 'Camera Installations & NVR Security Setup',
    category: 'network',
    categoryLabel: 'Network Engineering',
    icon: <Camera className="w-5 h-5 text-amber-400" />,
    typicalHoursMin: 3,
    typicalHoursMax: 8,
    timeDisplay: '3 – 8 Hours',
    rateType: 'consulting',
    description: 'PoE IP security camera mounting, NVR / DVR storage configuration, mobile app remote access, and wire concealment.'
  },
  {
    id: 'starlink-install',
    title: 'Starlink Internet Setup & Installation',
    category: 'network',
    categoryLabel: 'Network Engineering',
    icon: <Wifi className="w-5 h-5 text-amber-400" />,
    typicalHoursMin: 1.5,
    typicalHoursMax: 3,
    timeDisplay: '1.5 – 3 Hours',
    rateType: 'consulting',
    description: 'Dish mounting assistance, router bypass mode, ethernet adapter setup, and whole-home Wi-Fi mesh integration.'
  },
  {
    id: 'firewall-hardening',
    title: 'Firewall Configuration & Security Hardening',
    category: 'network',
    categoryLabel: 'Network Engineering',
    icon: <ShieldCheck className="w-5 h-5 text-amber-400" />,
    typicalHoursMin: 2,
    typicalHoursMax: 5,
    timeDisplay: '2 – 5 Hours',
    rateType: 'consulting',
    description: 'Enterprise firewall setup, VPN gateway routing, intrusion prevention rules, and perimeter security hardening.'
  },
  {
    id: 'custom-website',
    title: 'Website Builds & Custom Web Development',
    category: 'web',
    categoryLabel: 'Website Builds',
    icon: <Globe className="w-5 h-5 text-brand-400" />,
    typicalHoursMin: 25,
    typicalHoursMax: 40,
    timeDisplay: '1 – 3 Weeks Project Timeline',
    rateType: 'project',
    fixedPrice: 2000,
    description: 'Builds a modern, mobile-friendly website tailored to your business with lead forms and SEO optimization.'
  }
];

export type EstimatorRateTier = 'all' | 'remote' | 'bench' | 'licking' | 'county' | 'regional' | 'consulting' | 'web';

export interface PriceEstimatorPageProps {
  onNavigateToQuote: (initialProblem?: string, preferredMode?: 'onsite' | 'pickup' | 'remote') => void;
  onNavigateToPrices?: () => void;
  onViewTerms?: () => void;
}

export const PriceEstimatorPage: React.FC<PriceEstimatorPageProps> = ({
  onNavigateToQuote,
  onNavigateToPrices,
  onViewTerms
}) => {
  // Estimator State - Defaults to 'all' to show all services immediately
  const [estimatorRateContext, setEstimatorRateContext] = useState<EstimatorRateTier>('all');
  const [estimatorCategory, setEstimatorCategory] = useState<'all' | 'pc' | 'mobile' | 'network' | 'data' | 'business' | 'web'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);

  // Filtered jobs: When a rate tier is selected, only show the selected services for that tier!
  const filteredJobs = useMemo(() => {
    return ESTIMATED_JOBS.filter(job => {
      // 1. Tier Filter
      let matchesTier = true;
      if (
        estimatorRateContext === 'remote' ||
        estimatorRateContext === 'bench' || 
        estimatorRateContext === 'licking' || 
        estimatorRateContext === 'county' || 
        estimatorRateContext === 'regional'
      ) {
        matchesTier = job.rateType === 'standard';
      } else if (estimatorRateContext === 'consulting') {
        matchesTier = job.rateType === 'consulting';
      } else if (estimatorRateContext === 'web') {
        matchesTier = job.rateType === 'project';
      } else if (estimatorRateContext === 'all') {
        matchesTier = true;
      }

      // 2. Category Tab Filter
      const matchesCategory = estimatorCategory === 'all' || job.category === estimatorCategory;

      // 3. Search Query Filter
      const matchesSearch = searchQuery.trim() === '' || 
        job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.categoryLabel.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesTier && matchesCategory && matchesSearch;
    });
  }, [estimatorRateContext, estimatorCategory, searchQuery]);

  // Helper to format currency nicely
  const formatCost = (num: number) => {
    return Number.isInteger(num) ? `$${num}` : `$${num.toFixed(2)}`;
  };

  // Price calculations based on rate context
  const getJobCostString = (job: EstimatedJob, context: EstimatorRateTier) => {
    if (job.rateType === 'project') {
      return `Starting at $${job.fixedPrice?.toLocaleString()}`;
    }
    
    if (job.rateType === 'consulting') {
      const minHours = Math.max(1, job.typicalHoursMin);
      const maxHours = Math.max(1, job.typicalHoursMax);
      const minCost = minHours * 200;
      const maxCost = maxHours * 200;
      if (minHours === maxHours) {
        return `${formatCost(minCost)} (1-Hr Min @ $200/hr)`;
      }
      return `${formatCost(minCost)} – ${formatCost(maxCost)} ($200/hr)`;
    }

    if (context === 'remote') {
      const minHalfHours = Math.max(1, Math.round(job.typicalHoursMin * 2));
      const maxHalfHours = Math.max(1, Math.round(job.typicalHoursMax * 2));
      const minCost = minHalfHours * 37.50;
      const maxCost = maxHalfHours * 37.50;
      if (minHalfHours === maxHalfHours) {
        return `${formatCost(minCost)} (${job.typicalHoursMin === 0.5 ? '30-Min Min' : `${job.typicalHoursMin}-Hr`} @ $37.50 / 30m Remote)`;
      }
      return `${formatCost(minCost)} – ${formatCost(maxCost)} ($37.50 / 30m Remote)`;
    }

    // Standard rate type
    let hourlyRate = 100;
    let tierLabel = 'Texas Co';

    if (context === 'bench') {
      hourlyRate = 75;
      tierLabel = 'Bench';
    } else if (context === 'licking') {
      hourlyRate = 75;
      tierLabel = 'Licking';
    } else if (context === 'regional') {
      hourlyRate = 150;
      tierLabel = 'Regional';
    } else if (context === 'county') {
      hourlyRate = 100;
      tierLabel = 'Texas Co';
    } else { // 'all'
      const minHours = Math.max(1, job.typicalHoursMin);
      const maxHours = Math.max(1, job.typicalHoursMax);
      return `${formatCost(minHours * 75)} – ${formatCost(maxHours * 150)} ($75–$150/hr)`;
    }

    const minHours = Math.max(1, job.typicalHoursMin);
    const maxHours = Math.max(1, job.typicalHoursMax);
    const minCost = minHours * hourlyRate;
    const maxCost = maxHours * hourlyRate;
    
    if (minHours === maxHours) {
      return `${formatCost(minCost)} (1-Hr Min @ $${hourlyRate}/hr ${tierLabel})`;
    }
    return `${formatCost(minCost)} – ${formatCost(maxCost)} ($${hourlyRate}/hr ${tierLabel})`;
  };

  // Toggle multi-select job for batch estimate
  const toggleJobSelection = (id: string) => {
    setSelectedJobIds(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  // Calculate totals for selected jobs
  const selectedJobsSummary = useMemo(() => {
    const selected = ESTIMATED_JOBS.filter(job => selectedJobIds.includes(job.id));
    if (selected.length === 0) return null;

    let totalMinHours = 0;
    let totalMaxHours = 0;
    let totalMinCost = 0;
    let totalMaxCost = 0;
    let hasWebProject = false;
    let nonWebTaskCount = 0;

    selected.forEach(job => {
      if (job.rateType === 'project') {
        hasWebProject = true;
        totalMinCost += (job.fixedPrice || 2000);
        totalMaxCost += (job.fixedPrice || 2000);
      } else {
        nonWebTaskCount++;
        const minH = Math.max(estimatorRateContext === 'remote' ? 0.5 : 1, job.typicalHoursMin);
        const maxH = Math.max(estimatorRateContext === 'remote' ? 0.5 : 1, job.typicalHoursMax);
        totalMinHours += minH;
        totalMaxHours += maxH;

        let rate = 100;
        if (job.rateType === 'consulting') {
          rate = 200;
        } else if (estimatorRateContext === 'remote') {
          rate = 75; // $37.50 / 30 min = $75/hr
        } else if (estimatorRateContext === 'bench' || estimatorRateContext === 'licking') {
          rate = 75;
        } else if (estimatorRateContext === 'county') {
          rate = 100;
        } else if (estimatorRateContext === 'regional') {
          rate = 150;
        }

        totalMinCost += minH * rate;
        totalMaxCost += maxH * rate;
      }
    });

    // Enforce minimum if non-web tasks are selected
    const baselineMinHours = estimatorRateContext === 'remote' ? 0.5 : 1;
    if (nonWebTaskCount > 0 && totalMinHours < baselineMinHours) {
      totalMinHours = baselineMinHours;
      totalMaxHours = Math.max(baselineMinHours, totalMaxHours);
      let rate = 100;
      if (estimatorRateContext === 'consulting') {
        rate = 200;
      } else if (estimatorRateContext === 'remote') {
        rate = 75;
      } else if (estimatorRateContext === 'bench' || estimatorRateContext === 'licking') {
        rate = 75;
      } else if (estimatorRateContext === 'county') {
        rate = 100;
      } else if (estimatorRateContext === 'regional') {
        rate = 150;
      }
      totalMinCost = baselineMinHours * rate;
      totalMaxCost = Math.max(baselineMinHours * rate, totalMaxCost);
    }

    return {
      count: selected.length,
      totalMinHours,
      totalMaxHours,
      totalMinCost,
      totalMaxCost,
      hasWebProject,
      selected
    };
  }, [selectedJobIds, estimatorRateContext]);

  // Handle prefill request quote
  const handleRequestQuoteForJob = (job: EstimatedJob) => {
    let rateText = '';
    if (job.rateType === 'project') {
      rateText = `From $${job.fixedPrice?.toLocaleString()} (Website Project Scope: 50% kickoff, 50% live launch)`;
    } else if (job.rateType === 'consulting') {
      rateText = 'Network Engineering @ $200/hr (1-hour minimum of $200, then $100.00 per 30 minutes thereafter, rounded up)';
    } else if (estimatorRateContext === 'regional') {
      rateText = 'Regional On-Site Support @ $150/hr (1-hour minimum of $150, then $75.00 per 30 minutes thereafter, rounded up)';
    } else if (estimatorRateContext === 'county') {
      rateText = 'Texas County On-Site Support @ $100/hr (1-hour minimum of $100, then $50.00 per 30 minutes thereafter, rounded up)';
    } else if (estimatorRateContext === 'licking') {
      rateText = 'Hometown On-Site Support (Licking & 10-Mile Radius) @ $75/hr (1-hour minimum of $75, then $37.50 per 30 minutes thereafter, rounded up)';
    } else if (estimatorRateContext === 'remote') {
      rateText = 'Remote Support @ $37.50 per 30 minutes (30-min minimum of $37.50, then $37.50 per 30 minutes thereafter, rounded up)';
    } else {
      rateText = 'In-Shop / Bench Service (Device Drop-Off) @ $75/hr (1-hour minimum of $75, then $37.50 per 30 minutes thereafter, rounded up)';
    }
    
    const prefill = `Service Request: ${job.title}
Typical Time Estimate: ${job.timeDisplay}
Service Rate: ${rateText}
Notes: ${estimatorRateContext === 'remote' ? 'Remote desktop session (30-min minimum of $37.50, billed in 30-min increments).' : (job.benchRecommended || estimatorRateContext === 'bench' ? 'Device drop-off recommended.' : (job.rateType === 'project' ? 'Custom website build project.' : 'Direct on-site visit with travel time baked in (1-hour minimum, then 30-minute increments thereafter, rounded up).'))}`;
    
    const mode = (estimatorRateContext === 'remote') ? 'remote' : ((job.benchRecommended || estimatorRateContext === 'bench') ? 'pickup' : 'onsite');
    onNavigateToQuote(prefill, mode);
  };

  const handleRequestQuoteForBatch = () => {
    if (!selectedJobsSummary) return;
    const taskNames = selectedJobsSummary.selected.map(j => `• ${j.title} (${j.timeDisplay})`).join('\n');
    let rateText = '$75/hr ($37.50 / 30 min In-Shop Bench / Licking)';
    if (estimatorRateContext === 'remote') {
      rateText = '$37.50 / 30 min Remote Support';
    } else if (estimatorRateContext === 'consulting') {
      rateText = '$200/hr ($100.00 / 30 min Network Engineering)';
    } else if (estimatorRateContext === 'county') {
      rateText = '$100/hr ($50.00 / 30 min Texas County On-Site)';
    } else if (estimatorRateContext === 'regional') {
      rateText = '$150/hr ($75.00 / 30 min Regional On-Site)';
    } else if (estimatorRateContext === 'web') {
      rateText = 'Project rate (From $2,000)';
    }
    
    const prefill = `Multi-Task Service Request:
${taskNames}

Estimated Total Time: ${selectedJobsSummary.totalMinHours === selectedJobsSummary.totalMaxHours ? `${selectedJobsSummary.totalMinHours} ${selectedJobsSummary.totalMinHours === 1 ? 'Hour' : 'Hours'}` : `${selectedJobsSummary.totalMinHours} – ${selectedJobsSummary.totalMaxHours} Hours`} (${estimatorRateContext === 'remote' ? '30-min min, 30-min increments' : '1-hr min, 30-min increments thereafter'})
Estimated Cost Range: ${formatCost(selectedJobsSummary.totalMinCost)} – ${formatCost(selectedJobsSummary.totalMaxCost)} (${rateText})
Notes: Pre-calculated via Job Time Estimator (${estimatorRateContext === 'remote' ? '30-minute minimum rate' : '1-hour minimum rate'}).`;
    
    onNavigateToQuote(prefill);
  };

  return (
    <div className="space-y-10 sm:space-y-12 max-w-5xl mx-auto px-2 sm:px-4 py-2">
      {/* Top Header */}
      <div className="text-center max-w-3xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-2 py-1 px-3.5 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-mono font-bold uppercase tracking-wider border border-emerald-500/20 select-none">
          <Calculator className="w-3.5 h-3.5 text-emerald-400" />
          Interactive Cost &amp; Time Calculator
        </div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-display font-black text-white tracking-tight">
          Price Estimator
        </h1>
        <p className="text-sm sm:text-base text-slate-300 max-w-2xl mx-auto leading-relaxed">
          See typical repair times and expected costs for common IT repairs, upgrades, and projects. All work is billed with a standard <strong className="text-white">1-hour minimum</strong>, then in <strong className="text-emerald-400">30-minute increments thereafter (rounded up)</strong> to prevent any unexpected surprise bills.
        </p>

        {/* Navigation Quick Links */}
        {onNavigateToPrices && (
          <div className="pt-2">
            <button
              onClick={onNavigateToPrices}
              className="text-xs font-mono text-brand-400 hover:text-brand-300 underline inline-flex items-center gap-1 cursor-pointer"
            >
              <DollarSign className="w-3.5 h-3.5" />
              <span>Looking for our complete rate card? View Prices &amp; Services Matrix</span>
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* Estimator Main Container Card */}
      <div className="p-5 sm:p-8 rounded-3xl bg-neutral-950/95 border border-neutral-800 shadow-2xl space-y-6">
        
        {/* Controls Bar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 rounded-2xl bg-neutral-900/60 border border-neutral-800">
          <div className="space-y-1.5">
            <label className="text-[11px] font-mono text-slate-400 font-bold uppercase tracking-wider block flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-brand-400" />
              Select Your Service Location / Rate Option:
            </label>
            <div className="flex flex-wrap items-center gap-1.5 select-none">
              <button
                type="button"
                onClick={() => { setEstimatorRateContext('all'); setEstimatorCategory('all'); }}
                className={`px-3 py-1.5 rounded-xl text-xs font-mono font-bold transition-all cursor-pointer ${
                  estimatorRateContext === 'all'
                    ? 'bg-brand-500 text-neutral-950 shadow-md font-extrabold'
                    : 'bg-neutral-800 text-slate-300 hover:text-white hover:bg-neutral-700'
                }`}
              >
                All Services / Rates
              </button>
              <button
                type="button"
                onClick={() => { setEstimatorRateContext('remote'); setEstimatorCategory('all'); }}
                className={`px-3 py-1.5 rounded-xl text-xs font-mono font-bold transition-all cursor-pointer ${
                  estimatorRateContext === 'remote'
                    ? 'bg-violet-500 text-neutral-950 shadow-md font-extrabold'
                    : 'bg-neutral-800 text-slate-300 hover:text-white hover:bg-neutral-700'
                }`}
              >
                Remote Support ($37.50 / 30m)
              </button>
              <button
                type="button"
                onClick={() => { setEstimatorRateContext('bench'); setEstimatorCategory('all'); }}
                className={`px-3 py-1.5 rounded-xl text-xs font-mono font-bold transition-all cursor-pointer ${
                  estimatorRateContext === 'bench'
                    ? 'bg-emerald-500 text-neutral-950 shadow-md font-extrabold'
                    : 'bg-neutral-800 text-slate-300 hover:text-white hover:bg-neutral-700'
                }`}
              >
                In-Shop / Bench ($75/hr)
              </button>
              <button
                type="button"
                onClick={() => { setEstimatorRateContext('licking'); setEstimatorCategory('all'); }}
                className={`px-3 py-1.5 rounded-xl text-xs font-mono font-bold transition-all cursor-pointer ${
                  estimatorRateContext === 'licking'
                    ? 'bg-teal-500 text-neutral-950 shadow-md font-extrabold'
                    : 'bg-neutral-800 text-slate-300 hover:text-white hover:bg-neutral-700'
                }`}
              >
                Hometown Licking & 10-Mi ($75/hr)
              </button>
              <button
                type="button"
                onClick={() => { setEstimatorRateContext('county'); setEstimatorCategory('all'); }}
                className={`px-3 py-1.5 rounded-xl text-xs font-mono font-bold transition-all cursor-pointer ${
                  estimatorRateContext === 'county'
                    ? 'bg-cyan-500 text-neutral-950 shadow-md font-extrabold'
                    : 'bg-neutral-800 text-slate-300 hover:text-white hover:bg-neutral-700'
                }`}
              >
                Texas County ($100/hr)
              </button>
              <button
                type="button"
                onClick={() => { setEstimatorRateContext('regional'); setEstimatorCategory('all'); }}
                className={`px-3 py-1.5 rounded-xl text-xs font-mono font-bold transition-all cursor-pointer ${
                  estimatorRateContext === 'regional'
                    ? 'bg-sky-500 text-neutral-950 shadow-md font-extrabold'
                    : 'bg-neutral-800 text-slate-300 hover:text-white hover:bg-neutral-700'
                }`}
              >
                Regional Route ($150/hr)
              </button>
              <button
                type="button"
                onClick={() => { setEstimatorRateContext('consulting'); setEstimatorCategory('all'); }}
                className={`px-3 py-1.5 rounded-xl text-xs font-mono font-bold transition-all cursor-pointer ${
                  estimatorRateContext === 'consulting'
                    ? 'bg-amber-500 text-neutral-950 shadow-md font-extrabold'
                    : 'bg-neutral-800 text-slate-300 hover:text-white hover:bg-neutral-700'
                }`}
              >
                Network Engineering ($200/hr)
              </button>
            </div>
          </div>

          <div className="relative min-w-[240px]">
            <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search repair or service..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-8 py-2 rounded-xl bg-neutral-950 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs text-white placeholder-slate-500"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Category Filter Tabs */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-thin text-xs">
          {[
            { id: 'all', label: 'All Repairs & Tasks' },
            { id: 'pc', label: 'PC & Laptop' },
            { id: 'mobile', label: 'Phone & Mobile' },
            { id: 'network', label: 'Wi-Fi & Networking' },
            { id: 'data', label: 'Data & Backup' },
            { id: 'business', label: 'Business & Servers' },
            { id: 'web', label: 'Websites' }
          ].map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setEstimatorCategory(tab.id as any)}
              className={`px-3 py-1.5 rounded-xl font-mono text-xs whitespace-nowrap transition-all cursor-pointer font-bold ${
                estimatorCategory === tab.id
                  ? 'bg-neutral-200 text-neutral-950 shadow-sm'
                  : 'bg-neutral-900/80 text-slate-400 hover:text-slate-200 hover:bg-neutral-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Highlight Strip */}
        <div className="p-3.5 rounded-2xl bg-gradient-to-r from-emerald-950/30 via-neutral-900 to-cyan-950/30 border border-emerald-500/20 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-emerald-500/20 text-emerald-400 shrink-0">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <p className="text-slate-300 text-xs leading-relaxed">
              <strong className="text-white">Minimum Rate &amp; 30-Minute Increments:</strong> Standard service calls carry a 1-hour minimum rate and are billed in 30-minute increments thereafter.
            </p>
          </div>
          <div className="text-[11px] font-mono text-emerald-400 shrink-0 self-end sm:self-auto font-semibold">
            {estimatorRateContext === 'remote' ? '✓ 30-Min Min • $37.50 / 30m' : '✓ 1-Hr Min • 30-Min Increments'}
          </div>
        </div>

        {/* Multi-Task Summary */}
        <AnimatePresence>
          {selectedJobsSummary && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-brand-950/40 via-neutral-900 to-amber-950/40 border border-brand-500/40 shadow-xl space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-neutral-800/80 pb-3">
                  <div className="flex items-center gap-2">
                    <Calculator className="w-4 h-4 text-brand-400" />
                    <span className="text-xs font-mono font-bold text-white uppercase tracking-wider">
                      Combined Total ({selectedJobsSummary.count} jobs)
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedJobIds([])}
                    className="text-[11px] font-mono text-slate-400 hover:text-rose-400 underline cursor-pointer"
                  >
                    Clear Selection
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                  <div className="p-3 rounded-xl bg-neutral-950/80 border border-neutral-800">
                    <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider block">Estimated Combined Time</span>
                    <span className="text-base font-display font-black text-white mt-0.5 block">
                      {selectedJobsSummary.totalMinHours === selectedJobsSummary.totalMaxHours 
                        ? `${selectedJobsSummary.totalMinHours} Hours` 
                        : `${selectedJobsSummary.totalMinHours} – ${selectedJobsSummary.totalMaxHours} Hours`}
                    </span>
                  </div>

                  <div className="p-3 rounded-xl bg-neutral-950/80 border border-neutral-800">
                    <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider block">Estimated Price Range</span>
                    <span className="text-base font-display font-black text-emerald-400 mt-0.5 block">
                      {formatCost(selectedJobsSummary.totalMinCost)} – {formatCost(selectedJobsSummary.totalMaxCost)}
                    </span>
                  </div>

                  <div className="flex items-center">
                    <button
                      onClick={handleRequestQuoteForBatch}
                      className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-brand-500 hover:from-emerald-400 hover:to-brand-400 text-neutral-950 font-display font-extrabold text-xs uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer transition-all active:scale-95 shadow-md"
                    >
                      <Wrench className="w-4 h-4" />
                      <span>Request Quote</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Estimated Jobs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredJobs.length === 0 ? (
            <div className="col-span-full py-12 text-center text-slate-500">No services match your search.</div>
          ) : (
            filteredJobs.map(job => {
              const isSelected = selectedJobIds.includes(job.id);
              return (
                <div
                  key={job.id}
                  className={`p-4 sm:p-5 rounded-2xl border transition-all duration-200 flex flex-col justify-between space-y-3 text-left relative ${
                    isSelected 
                      ? 'bg-neutral-900 border-brand-500/80 shadow-lg ring-1 ring-brand-500/40' 
                      : 'bg-neutral-900/40 border-neutral-800/80 hover:border-neutral-700 hover:bg-neutral-900/70'
                  }`}
                >
                  <div className="space-y-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded-xl bg-neutral-950 border border-neutral-800">
                          {job.icon}
                        </div>
                        <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">
                          {job.categoryLabel}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleJobSelection(job.id)}
                        className={`text-[11px] font-mono px-2 py-1 rounded-lg border flex items-center gap-1 transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-brand-500/20 text-brand-300 border-brand-500/50'
                            : 'bg-neutral-950 text-slate-400 border-neutral-800 hover:border-slate-600'
                        }`}
                      >
                        {isSelected ? '✓ Selected' : '+ Add to Total'}
                      </button>
                    </div>
                    <h3 className="text-sm sm:text-base font-display font-bold text-white leading-snug">
                      {job.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <div className="flex items-center gap-1.5 py-1 px-2.5 rounded-lg bg-neutral-950 border border-neutral-800 text-xs font-mono text-slate-300">
                        <Clock className="w-3.5 h-3.5 text-brand-400" />
                        <span>{job.timeDisplay}</span>
                      </div>
                      <div className="flex items-center gap-1 py-1 px-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-xs font-mono text-emerald-400 font-bold">
                        <span>{getJobCostString(job, estimatorRateContext)}</span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-300 leading-relaxed pt-1 line-clamp-2">
                      {job.description}
                    </p>
                  </div>
                  <div className="pt-3 border-t border-neutral-800/70 flex items-center justify-end">
                    <button
                      type="button"
                      onClick={() => handleRequestQuoteForJob(job)}
                      className="px-3.5 py-1.5 rounded-xl bg-neutral-800 hover:bg-neutral-700 text-white font-mono text-[11px] font-bold flex items-center gap-1.5 transition-all cursor-pointer"
                    >
                      <span>Request Quote</span>
                      <ArrowRight className="w-3 h-3 text-brand-400" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default PriceEstimatorPage;
